import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Users, Settings } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ConfigurePopup = ({ 
  isOpen, 
  onClose, 
  eligibleCandidates = [], 
  onSave = null,
  initialData = null 
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("timing");
  
  const [formData, setFormData] = useState({
    // Timing Configuration
    batchPerUsers: "",
    eventStartDate: "",
    eventStartTime: "",
    eventEndDate: "",
    eventEndTime: "",
    examStartTime: "",
    duration: "",
    gap: "",
    graceTime: "",
    sendInvitationEmail: true, // Default to true
    
    // Security Settings
    enableProctoring: false,
    captureImages: false,
    trackTabSwitching: false,
    faceDetection: false,
    warningsAllowed: 0,
    
    // Advanced Settings
    language: "English",
    passingScore: [60],
    captureFrequency: 10, // Capture frequency in seconds (default 10 seconds)
  });

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
    
    // Set default batch size to number of eligible candidates when modal opens
    if (isOpen && eligibleCandidates.length > 0) {
      setFormData(prev => ({
        ...prev,
        // Default to all candidates per day (single batch)
        batchPerUsers: prev.batchPerUsers || eligibleCandidates.length.toString()
      }));
    }
  }, [isOpen, initialData, eligibleCandidates.length]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // If disabling proctoring, also disable all dependent security features
      if (field === "enableProctoring" && !value) {
        newData.captureImages = false;
        newData.trackTabSwitching = false;
        newData.faceDetection = false;
        // Reset capture frequency to default when proctoring is disabled
        newData.captureFrequency = 10;
      }
      
      // Auto-set exam start time to event start time if not set
      if (field === "eventStartTime" && !newData.examStartTime) {
        newData.examStartTime = value;
      }
      
      return newData;
    });
  };

  const calculateAssignments = () => {
    const {
      batchPerUsers,
      eventStartDate,
      eventStartTime,
      eventEndDate,
      eventEndTime,
      examStartTime,
      duration,
      gap,
      graceTime
    } = formData;

    // Validation
    if (!batchPerUsers || !eventStartDate || !eventStartTime || !eventEndDate || !eventEndTime || !duration || !gap) {
      return { error: "Please fill in all required timing fields" };
    }

    const batchSize = parseInt(batchPerUsers);
    const examDuration = parseInt(duration);
    const gapMinutes = parseInt(gap);
    const totalEligible = eligibleCandidates.length;


    if (totalEligible === 0) {
      return { error: "No eligible candidates available for exam assignment" };
    }

    if (batchSize <= 0) {
      return { error: "Batch size must be greater than 0" };
    }

    // If batch size is not specified, use all eligible candidates
    // Batch size controls candidates per day, not total candidates to show
    const candidatesPerBatch = batchSize || totalEligible;
    const totalCandidatesToShow = totalEligible; // Always show all eligible candidates

    // Parse event window
    const eventStart = new Date(`${eventStartDate}T${eventStartTime}`);
    const eventEnd = new Date(`${eventEndDate}T${eventEndTime}`);
    const examStart = new Date(`${eventStartDate}T${examStartTime || eventStartTime}`);

    // Validate dates
    if (eventStart >= eventEnd) {
      return { error: "Event end time must be after event start time" };
    }

    // Calculate available time window in minutes
    const availableMinutes = (eventEnd - eventStart) / (1000 * 60);

    if (availableMinutes < examDuration) {
      return { error: "Event window is too short for the exam duration" };
    }

    // Calculate how many candidates can be assigned per day based on time constraints
    const maxCandidatesPerDayByTime = Math.floor((availableMinutes - examDuration) / gapMinutes) + 1;
    
    // The actual candidates per day is the minimum of batch size and time constraints
    const actualCandidatesPerDay = Math.min(candidatesPerBatch, maxCandidatesPerDayByTime);

    if (actualCandidatesPerDay <= 0) {
      return { error: "Gap time is too large for the available event window" };
    }

    // Generate assignment cards
    const cards = [];
    let currentDate = new Date(examStart);
    let candidateIndex = 0;
    let candidatesAssignedToday = 0;
    let currentDay = 1;


    while (candidateIndex < totalCandidatesToShow) {
      const candidate = eligibleCandidates[candidateIndex];
      
      
      // Check if we need to move to next day
      if (candidatesAssignedToday >= actualCandidatesPerDay) {
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
        currentDate.setHours(eventStart.getHours(), eventStart.getMinutes(), 0, 0);
        candidatesAssignedToday = 0;
        currentDay++;
      }
      
      // Calculate exam time for this candidate
      const candidateExamTime = new Date(currentDate);
      const minutesOffset = candidatesAssignedToday * gapMinutes;
      candidateExamTime.setMinutes(candidateExamTime.getMinutes() + minutesOffset);

      cards.push({
        batchNumber: currentDay, // Use day number as batch number
        candidateId: candidate._id,
        candidateName: candidate.name,
        candidateEmail: candidate.email,
        examDate: candidateExamTime.toISOString().split('T')[0],
        examTime: candidateExamTime.toTimeString().slice(0, 5),
        examDateTime: candidateExamTime.toISOString(),
        duration: examDuration,
        graceTime: parseInt(graceTime) || 60,
        sendInvitationEmail: formData.sendInvitationEmail !== false, // Include email setting
        // Copy security settings
        enableProctoring: formData.enableProctoring,
        captureImages: formData.captureImages,
        trackTabSwitching: formData.trackTabSwitching,
        faceDetection: formData.faceDetection,
        warningsAllowed: formData.warningsAllowed,
        // Copy advanced settings
        language: formData.language,
        passingScore: formData.passingScore[0] || 60,
        captureFrequency: formData.captureFrequency || 10,
      });

      candidateIndex++;
      candidatesAssignedToday++;
    }


    return { cards, totalAssigned: cards.length, totalEligible };
  };

  const handleSave = () => {
    // Calculate and show assignment cards
    
    const result = calculateAssignments();
    
    
    if (result.error) {
      // Show error in a more user-friendly way
      toast({
        title: "Configuration Error",
        description: result.error,
        variant: "destructive",
      });
      return;
    }

    if (result.cards.length === 0) {
      toast({
        title: "No Assignments Generated",
        description: "No exam assignments could be generated with the current configuration",
        variant: "destructive",
      });
      return;
    }


    // Instead of showing cards in popup, pass them to parent and close popup
    if (onSave) {
      onSave({
        formData,
        assignments: result.cards,
        isPreview: true // Flag to indicate this is preview mode
      });
    }
    onClose();
  };

  const handleCancel = () => {
    // Reset form data and close
    setFormData({
      batchPerUsers: "",
      eventStartDate: "",
      eventStartTime: "",
      eventEndDate: "",
      eventEndTime: "",
      examStartTime: "",
      duration: "",
      gap: "",
      graceTime: "",
      sendInvitationEmail: true, // Default to true
      enableProctoring: false,
      captureImages: false,
      trackTabSwitching: false,
      faceDetection: false,
      warningsAllowed: 0,
      language: "English",
      passingScore: [60],
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Exam Configuration
            {eligibleCandidates.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                <Users className="h-3 w-3 mr-1" />
                {eligibleCandidates.length} Eligible Candidates
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="p-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="timing">Timing</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
                <TabsTrigger value="advanced">Advanced</TabsTrigger>
              </TabsList>
              
              <TabsContent value="timing" className="space-y-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Timing Configuration</h3>
                
                {/* Eligible Candidates Display */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold text-blue-900">Eligible Candidates</span>
                  </div>
                  <p className="text-sm text-blue-700">
                    Total: <span className="font-bold">{eligibleCandidates.length}</span> candidates will receive exam assignments
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Only candidates meeting eligibility criteria will be assigned exams
                  </p>
                </div>

                {/* Batch per Users */}
                <div className="space-y-2">
                  <Label htmlFor="batchPerUsers" className="text-sm font-medium">
                    Candidates per Day <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="batchPerUsers"
                    type="number"
                    min="1"
                    max={eligibleCandidates.length}
                    placeholder={`Enter candidates per day (max: ${eligibleCandidates.length})`}
                    value={formData.batchPerUsers}
                    onChange={(e) => handleInputChange("batchPerUsers", e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    How many candidates can take the exam per day. All {eligibleCandidates.length} eligible candidates will be scheduled across multiple days if needed.
                  </p>
                  <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                    <strong>Examples:</strong><br/>
                    • 1 candidate/day → 3 candidates across 3 days<br/>
                    • 2 candidates/day → 2 candidates on day 1, 1 candidate on day 2<br/>
                    • 3 candidates/day → All 3 candidates on the same day
                  </div>
                </div>

                {/* Event Start Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="eventStartDate" className="text-sm font-medium">
                      Event Start Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="eventStartDate"
                      type="date"
                      value={formData.eventStartDate}
                      onChange={(e) => handleInputChange("eventStartDate", e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eventStartTime" className="text-sm font-medium">
                      Event Start Time <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="eventStartTime"
                      type="time"
                      value={formData.eventStartTime}
                      onChange={(e) => handleInputChange("eventStartTime", e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Event End Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="eventEndDate" className="text-sm font-medium">
                      Event End Date <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="eventEndDate"
                      type="date"
                      value={formData.eventEndDate}
                      onChange={(e) => handleInputChange("eventEndDate", e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eventEndTime" className="text-sm font-medium">
                      Event End Time <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="eventEndTime"
                      type="time"
                      value={formData.eventEndTime}
                      onChange={(e) => handleInputChange("eventEndTime", e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Exam Start Time */}
                <div className="space-y-2">
                  <Label htmlFor="examStartTime" className="text-sm font-medium">
                    Exam Start Time
                  </Label>
                  <Input
                    id="examStartTime"
                    type="time"
                    value={formData.examStartTime}
                    onChange={(e) => handleInputChange("examStartTime", e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Defaults to Event Start Time if not specified
                  </p>
                </div>

                {/* Duration */}
                <div className="space-y-2">
                  <Label htmlFor="duration" className="text-sm font-medium">
                    Exam Duration (minutes) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    placeholder="Enter duration in minutes"
                    value={formData.duration}
                    onChange={(e) => handleInputChange("duration", e.target.value)}
                    className="w-full"
                  />
                </div>

                {/* Gap */}
                <div className="space-y-2">
                  <Label htmlFor="gap" className="text-sm font-medium">
                    Gap (minutes) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="gap"
                    type="number"
                    min="1"
                    placeholder="Enter gap in minutes"
                    value={formData.gap}
                    onChange={(e) => handleInputChange("gap", e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Time interval between consecutive candidate assignments
                  </p>
                </div>

                {/* Grace Time */}
                <div className="space-y-2">
                  <Label htmlFor="graceTime" className="text-sm font-medium">
                    Grace Time (minutes)
                  </Label>
                  <Input
                    id="graceTime"
                    type="number"
                    min="0"
                    placeholder="Enter grace time in minutes"
                    value={formData.graceTime}
                    onChange={(e) => handleInputChange("graceTime", e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Additional time allowed after exam duration
                  </p>
                </div>

                {/* Send Invitation Email */}
                <div className="space-y-3 p-4 border border-green-200 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="sendInvitationEmail" className="text-sm font-medium text-green-900">
                        Send Invitation Email
                      </Label>
                      <p className="text-xs text-green-700">
                        Automatically send exam invitation emails to candidates
                      </p>
                    </div>
                    <Switch
                      id="sendInvitationEmail"
                      checked={formData.sendInvitationEmail !== false} // Default to true
                      onCheckedChange={(checked) => handleInputChange("sendInvitationEmail", checked)}
                    />
                  </div>
                  <p className="text-xs text-green-600">
                    When enabled, candidates will receive email notifications with exam details and access links
                  </p>
                </div>
              </TabsContent>
              
              <TabsContent value="security" className="space-y-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Security Configuration</h3>
                
                <div className="space-y-6">
                  {/* Enable Proctoring - Main Toggle */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label htmlFor="enableProctoring" className="text-sm font-medium">
                          Enable Proctoring
                        </Label>
                        <p className="text-xs text-gray-500">
                          Monitor candidates during the exam
                        </p>
                      </div>
                      <Switch
                        id="enableProctoring"
                        checked={formData.enableProctoring}
                        onCheckedChange={(checked) => handleInputChange("enableProctoring", checked)}
                      />
                    </div>
                  </div>

                  {/* Conditional Security Options - Only show when proctoring is enabled */}
                  {formData.enableProctoring && (
                    <div className="space-y-4 pl-4 border-l-2 border-blue-200 bg-blue-50 p-4 rounded-r-lg">
                      <p className="text-sm text-blue-700 font-medium mb-3">
                        Proctoring Options
                      </p>
                      
                      {/* Capture Images */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="captureImages" className="text-sm font-medium">
                            Capture Images
                          </Label>
                          <p className="text-xs text-gray-500">
                            Take periodic screenshots during exam
                          </p>
                        </div>
                        <Switch
                          id="captureImages"
                          checked={formData.captureImages}
                          onCheckedChange={(checked) => handleInputChange("captureImages", checked)}
                        />
                      </div>

                      {/* Track Tab Switching */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="trackTabSwitching" className="text-sm font-medium">
                            Track Tab Switching
                          </Label>
                          <p className="text-xs text-gray-500">
                            Monitor when candidates switch browser tabs
                          </p>
                        </div>
                        <Switch
                          id="trackTabSwitching"
                          checked={formData.trackTabSwitching}
                          onCheckedChange={(checked) => handleInputChange("trackTabSwitching", checked)}
                        />
                      </div>

                      {/* Face Detection */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="faceDetection" className="text-sm font-medium">
                            Face Detection
                          </Label>
                          <p className="text-xs text-gray-500">
                            Ensure candidate presence throughout exam
                          </p>
                        </div>
                        <Switch
                          id="faceDetection"
                          checked={formData.faceDetection}
                          onCheckedChange={(checked) => handleInputChange("faceDetection", checked)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Warnings Allowed - Always visible */}
                  <div className="space-y-3">
                    <Label htmlFor="warningsAllowed" className="text-sm font-medium">
                      Warnings Allowed
                    </Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Number of warnings before disqualification
                    </p>
                    <Input
                      id="warningsAllowed"
                      type="number"
                      min="0"
                      max="10"
                      placeholder="0"
                      value={formData.warningsAllowed}
                      onChange={(e) => handleInputChange("warningsAllowed", parseInt(e.target.value) || 0)}
                      className="w-full"
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="advanced" className="space-y-4 mt-4">
                <h3 className="text-lg font-semibold mb-4">Advanced Configuration</h3>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Language Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Language</Label>
                      <Select
                        value={formData.language}
                        onValueChange={(value) => handleInputChange("language", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="English">English</SelectItem>
                          <SelectItem value="Spanish">Spanish</SelectItem>
                          <SelectItem value="French">French</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Passing Score */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Passing Score (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="60"
                        value={formData.passingScore?.[0] || 60}
                        onChange={(e) => handleInputChange("passingScore", [parseInt(e.target.value) || 60])}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Capture Frequency - Only show when proctoring is enabled */}
                  {formData.enableProctoring && (
                    <div className="space-y-4 p-4 border border-blue-200 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Settings className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-blue-900">Proctoring Settings</span>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="captureFrequency" className="text-sm font-medium">
                          Capture Frequency (seconds)
                        </Label>
                        <Input
                          id="captureFrequency"
                          type="number"
                          min="5"
                          max="300"
                          placeholder="10"
                          value={formData.captureFrequency}
                          onChange={(e) => handleInputChange("captureFrequency", parseInt(e.target.value) || 10)}
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500">
                          How often to capture screenshots during the exam (5-300 seconds)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

        <DialogFooter className="flex justify-between p-4">
          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="px-4 py-2"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Generate Schedule
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigurePopup;